import { create } from "zustand";
import { Track } from "@/types/track";
import { ArtistView } from "./artists-store";
import { AlbumView } from "./albums-store";
import { apiClient } from "./api-client";
import type { StorefrontAccent } from "./storefront-accent";
import { supabase } from "./supabase";

export interface Storefront {
  id: string;
  label_tag: string;
  slug: string;
  name: string;
  description: string | null;
  is_public: boolean;
  share_token: string | null;
  created_at: string;
  updated_at: string;
  label_name?: string;  // Human-readable label name (enriched by share endpoint)
  accent?: StorefrontAccent;  // The page's accent colour name (storefronts.accent); see lib/storefront-accent.ts
  // Branding of the storefront's OWN label (share endpoint): the label's cover
  // image, and its organization's logo and website. Never the viewer's label.
  banner_url?: string | null;
  logo_url?: string | null;
  website?: string | null;
}

/** What the owner learns from a storefront (GET /api/storefronts/:id/insights). */
export interface StorefrontInsights {
  window_days: number;
  since: string;
  catalog_tracks: number;
  searches: {
    searches: number;
    buyers: number;
    searches_with_no_results: number;
    popular_phrases: Array<{ phrase: string; buyers: number; searches: number }>;
  };
  most_shown_tracks: Array<{ track_id: string; title: string; count: number }>;
  license_requests: {
    requests: number;
    requested_tracks: Array<{ track_id: string; title: string; count: number }>;
  };
  phrase_min_buyers: number;
  not_tracked: string[];
}

export function fetchStorefrontInsights(storefrontId: string): Promise<StorefrontInsights> {
  return apiClient.get<StorefrontInsights>(`/api/storefronts/${storefrontId}/insights`);
}

/** Set the order the starred tracks lead every listing in. */
export function saveFeaturedOrder(storefrontId: string, trackIds: string[]) {
  return apiClient.put<{ status: string; updated: number }>(
    `/api/storefronts/${storefrontId}/featured-order`,
    { track_ids: trackIds }
  );
}

/** Replace the share link; the old one stops working. Returns the new token. */
export async function replaceShareLink(storefrontId: string): Promise<string> {
  const { share_token } = await apiClient.post<{ share_token: string }>(
    `/api/storefronts/${storefrontId}/share-link`,
    {}
  );
  useStorefrontStore.setState((state) =>
    state.storefront?.id === storefrontId
      ? { storefront: { ...state.storefront, share_token } }
      : {}
  );
  return share_token;
}

// Extended type for listing view (from GET /api/storefronts?mode=buyer)
export interface StorefrontListItem extends Storefront {
  track_covers: string[];   // First 5 track cover URLs
  label_name: string;       // Human-readable label name
  track_count: number;      // Total tracks in storefront
}

// Extended type for detail view (from GET /api/storefronts/id/:id)
export interface StorefrontDetail extends Storefront {
  is_owner: boolean;        // Whether current user owns this storefront
}

// Input type for creating storefront
export interface CreateStorefrontInput {
  name: string;
  description?: string;
  addAllTracks?: boolean;
}

// Helper function to map database track to frontend Track type (DRY)
function mapDatabaseTrack(dbTrack: any): Track {
  return {
    url: "", // Playback uses backend signed URL endpoint
    id: dbTrack.id,
    created_at: dbTrack.created_at,
    updated_at: dbTrack.updated_at,
    title: dbTrack.title || "Unknown Title",
    user_id: dbTrack.user_id,
    is_published: dbTrack.is_published,
    artist: dbTrack.artist || "",
    progress: 100,
    lastModified: dbTrack.updated_at || dbTrack.created_at,
    duration: typeof dbTrack.duration === "string"
      ? dbTrack.duration
      : String(dbTrack.duration || ""),
    metadata: dbTrack.metadata || {},
    tags: Array.isArray(dbTrack.tags) ? dbTrack.tags : [],
    writers: Array.isArray(dbTrack.writers) ? dbTrack.writers : [],
    lyrics: dbTrack.lyrics || "",
    analysis: dbTrack.analysis || null,
    cover_art_url: dbTrack.cover_art_url || dbTrack.artwork_url,
    artwork_url: dbTrack.cover_art_url || dbTrack.artwork_url,
    // Include all other fields from the database
    ...dbTrack,
  };
}

// Phase 8.2: Granular loading states for independent section rendering
export interface LoadingStates {
  storefronts: boolean;        // Seller: label storefronts list
  publicStorefronts: boolean;  // Buyer: public storefronts list
  storefront: boolean;         // Detail: storefront metadata + tracks
  artists: boolean;            // Detail: artists section
  albums: boolean;             // Detail: albums section
}

// Phase 8.2: Per-section errors for partial data display
export interface SectionErrors {
  storefronts: string | null;
  publicStorefronts: string | null;
  storefront: string | null;
  artists: string | null;
  albums: string | null;
}

// Phase B: Pagination state for Browse pages (independent data loading)
export interface PaginationState<T> {
  items: T[];
  total: number;
  offset: number;
  hasMore: boolean;
  isLoading: boolean;
  isFetchingMore: boolean;
  error: string | null;
  storefrontId: string | null;
}

export interface BrowsePagination {
  tracks: PaginationState<Track>;
  artists: PaginationState<ArtistView>;
  albums: PaginationState<AlbumView>;
}

// Phase B: Initial pagination state factory
const createInitialPaginationState = <T>(): PaginationState<T> => ({
  items: [],
  total: 0,
  offset: 0,
  hasMore: false,
  isLoading: false,
  isFetchingMore: false,
  error: null,
  storefrontId: null,
});

const initialBrowse: BrowsePagination = {
  tracks: createInitialPaginationState<Track>(),
  artists: createInitialPaginationState<ArtistView>(),
  albums: createInitialPaginationState<AlbumView>(),
};

interface StorefrontState {
  storefront: Storefront | null;
  storefronts: Storefront[];  // Multiple storefronts for Phase 2
  tracks: Track[];
  artists: ArtistView[];  // Artists whose tracks are in storefront catalog
  albums: AlbumView[];  // Albums whose tracks are in storefront catalog

  // Phase 8.2: Granular loading and error states
  loadingStates: LoadingStates;
  sectionErrors: SectionErrors;

  // Backward compatibility: computed from loadingStates (true if ANY section loading)
  isLoading: boolean;
  error: string | null;  // Legacy: first non-null error from sectionErrors

  // Public storefronts state (for buyer browsing)
  publicStorefronts: StorefrontListItem[];
  publicStorefrontsTotal: number;

  // Timestamps for reload prevention (Phase 8.1)
  storefrontsLoadedAt: number | null;
  storefrontsLabelTag: string | null;  // Track which label the storefronts were loaded for
  storefrontsSearch: string | null;  // Track current search context to prevent load-more race conditions
  publicStorefrontsLoadedAt: number | null;
  publicStorefrontsSearch: string | null;  // Track current search context for buyer storefronts

  // Phase B: Browse pages pagination state (independent from detail view)
  browse: BrowsePagination;

  // Phase B: Totals for stats display (populated by detail fetch)
  tracksTotal: number;
  artistsTotal: number;
  albumsTotal: number;

  // Actions
  storefrontsTotal: number;
  fetchLabelStorefronts: (labelTag: string, options?: { search?: string; limit?: number; offset?: number; force?: boolean }) => Promise<void>;  // Fetch storefronts for a label (paginated, server-side search)
  fetchStorefrontByShareToken: (slug: string, shareToken: string, options?: { tracksLimit?: number }) => Promise<void>;
  fetchStorefrontById: (storefrontId: string) => Promise<StorefrontDetail | null>;  // Fetch by ID with ownership info
  fetchStorefrontArtists: (storefrontId: string, options?: { limit?: number; search?: string; shareToken?: string }) => Promise<void>;  // Fetch artists from storefront catalog
  fetchStorefrontAlbums: (storefrontId: string, options?: { limit?: number; search?: string; shareToken?: string }) => Promise<void>;  // Fetch albums from storefront catalog
  fetchPublicStorefronts: (search?: string, limit?: number, offset?: number, force?: boolean) => Promise<void>;  // Buyer browse
  createStorefront: (data: CreateStorefrontInput) => Promise<Storefront | null>;  // Create new storefront
  updateStorefront: (storefrontId: string, updates: { name?: string; description?: string; is_public?: boolean; accent?: StorefrontAccent }) => Promise<Storefront | null>;  // Update storefront
  setStorefrontAccent: (storefrontId: string, accent: StorefrontAccent) => Promise<boolean>;  // Owner picks the page's accent; optimistic, reverts on failure
  deleteStorefront: (storefrontId: string) => Promise<boolean>;  // Delete storefront
  addTracksToCatalog: (storefrontId: string, trackIds: string[]) => Promise<{ success: boolean; error?: string }>;
  removeTrackFromCatalog: (storefrontId: string, trackId: string) => Promise<{ success: boolean; error?: string }>;
  refreshStorefrontPreview: () => Promise<void>;  // Reload the page's preview and counts after the catalog changes
  toggleTrackFeatured: (storefrontId: string, trackId: string, isFeatured: boolean) => Promise<{ success: boolean; error?: string }>;
  publishStorefront: (storefrontId: string, isPublic: boolean) => Promise<{ success: boolean; error?: string }>;
  updateStorefrontDescription: (
    storefrontId: string,
    description: string
  ) => Promise<{ success: boolean; error?: string }>;

  // Phase B: Browse pages pagination actions (independent from detail view)
  // Note: shareToken is required for viewer access on shared storefronts
  fetchBrowseTracks: (storefrontId: string, reset?: boolean, shareToken?: string, search?: string) => Promise<void>;
  fetchBrowseArtists: (storefrontId: string, reset?: boolean, shareToken?: string, search?: string) => Promise<void>;
  fetchBrowseAlbums: (storefrontId: string, reset?: boolean, shareToken?: string, search?: string) => Promise<void>;
  loadMoreBrowseTracks: (storefrontId: string, shareToken?: string, search?: string) => Promise<void>;
  loadMoreBrowseArtists: (storefrontId: string, shareToken?: string, search?: string) => Promise<void>;
  loadMoreBrowseAlbums: (storefrontId: string, shareToken?: string, search?: string) => Promise<void>;

  reset: () => void;
}

// What the storefront page loads: enough tracks for the 12-track featured chapter
// (starred come first) and the artwork wall; a shelf each of artists and albums.
export const STOREFRONT_TRACKS_PREVIEW_LIMIT = 24;
export const STOREFRONT_SHELF_PREVIEW_LIMIT = 8;

// Request deduplication cache
const requestCache = new Map<string, Promise<any>>();
const CACHE_DURATION = 30000; // 30 seconds
const cacheTimestamps = new Map<string, number>();

// Empties both maps. reset() calls it, so nothing cached for one viewer outlives a sign-out or an
// account switch.
export const clearStorefrontCache = () => {
  requestCache.clear();
  cacheTimestamps.clear();
};

/**
 * Who a storefront read is for: the session's user id, or "anon". The backend answers with
 * public-column track rows unless the caller is a fully set-up account (owner decision,
 * 2026-10-13), so a read in flight for one viewer must never be handed to another. Asked of the
 * Supabase client at call time, which is the session the request will carry, not of React state.
 */
async function storefrontViewerKey(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  return data.session?.user?.id ?? "anon";
}

/** The dedup key of a storefront-by-id read for `viewer`. Ids hold no "|", so the prefix is exact. */
function storefrontByIdKey(storefrontId: string, viewer: string): string {
  return `storefront-id-${storefrontId}|${viewer}`;
}

/** Drop every cached read whose key starts with `prefix`: every viewer's copy of one read. */
function forgetCachedReads(prefix: string) {
  for (const key of [...requestCache.keys(), ...cacheTimestamps.keys()]) {
    if (key.startsWith(prefix)) {
      requestCache.delete(key);
      cacheTimestamps.delete(key);
    }
  }
}

/** Drop every viewer's cached storefront-by-id read of this storefront (after an edit or a delete). */
function forgetStorefrontById(storefrontId: string) {
  forgetCachedReads(`storefront-id-${storefrontId}|`);
}

/**
 * The reads that write viewer-shaped track rows into the store. Each new one of a kind supersedes
 * the older ones still in flight, and reset() supersedes them all: an older read that lands later
 * is dropped, so a visitor who signed in or out (or another account) never gets the previous
 * viewer's rows back over their own.
 */
type ViewerShapedRead = "share" | "byId" | "browseTracks";
const latestRead: Record<ViewerShapedRead, number> = { share: 0, byId: 0, browseTracks: 0 };

/** Start a read of `kind`; the answer says whether it is still the latest when it lands. */
function startRead(kind: ViewerShapedRead): () => boolean {
  const read = ++latestRead[kind];
  return () => latestRead[kind] === read;
}

/** Join the latest read of `kind` (a load-more): dropped as well once a newer one starts. */
function joinRead(kind: ViewerShapedRead): () => boolean {
  const read = latestRead[kind];
  return () => latestRead[kind] === read;
}

/** The viewer the browse-tracks first page in flight was asked for. */
let browseTracksViewer: string | null = null;

// Phase 8.2: Initial loading states
const initialLoadingStates: LoadingStates = {
  storefronts: false,
  publicStorefronts: false,
  storefront: false,
  artists: false,
  albums: false,
};

// Phase 8.2: Initial section errors
const initialSectionErrors: SectionErrors = {
  storefronts: null,
  publicStorefronts: null,
  storefront: null,
  artists: null,
  albums: null,
};

// Phase 8.2: Helper to compute backward-compatible isLoading
const computeIsLoading = (states: LoadingStates): boolean =>
  Object.values(states).some(Boolean);

// Phase 8.2: Helper to get first non-null error for backward compatibility
const computeError = (errors: SectionErrors): string | null =>
  Object.values(errors).find((e) => e !== null) ?? null;

export const useStorefrontStore = create<StorefrontState>((set, get) => ({
  storefront: null,
  storefronts: [],
  tracks: [],
  artists: [],
  albums: [],

  // Phase 8.2: Granular loading and error states
  loadingStates: { ...initialLoadingStates },
  sectionErrors: { ...initialSectionErrors },

  // Backward compatibility
  isLoading: false,
  error: null,

  // Public storefronts state
  publicStorefronts: [],
  publicStorefrontsTotal: 0,

  // Seller storefronts total (for pagination)
  storefrontsTotal: 0,

  // Timestamps for reload prevention (Phase 8.1)
  storefrontsLoadedAt: null,
  storefrontsLabelTag: null,  // Track which label the storefronts were loaded for
  storefrontsSearch: null,  // Current search context for seller storefronts
  publicStorefrontsLoadedAt: null,
  publicStorefrontsSearch: null,  // Current search context for buyer storefronts

  // Phase B: Browse pages pagination state
  browse: { ...initialBrowse },

  // Phase B: Totals for stats display
  tracksTotal: 0,
  artistsTotal: 0,
  albumsTotal: 0,

  fetchLabelStorefronts: async (labelTag: string, options?: { search?: string; limit?: number; offset?: number; force?: boolean }) => {
    const { search, limit = 50, offset = 0, force = false } = options || {};
    const searchContext = search || "";
    const isLoadMore = offset > 0;
    const cacheKey = `storefronts-label-${labelTag}-${searchContext}-${limit}-${offset}`;
    const now = Date.now();
    const state = get();

    // Cache check: skip when loading more, forced, or search context changed
    if (!force && !isLoadMore && state.storefronts.length > 0 && state.storefrontsLoadedAt && state.storefrontsLabelTag === labelTag && state.storefrontsSearch === searchContext) {
      if (now - state.storefrontsLoadedAt < CACHE_DURATION) {
        return;
      }
    }

    if (force) {
      requestCache.delete(cacheKey);
      cacheTimestamps.delete(cacheKey);
    }

    // In-flight deduplication
    if (requestCache.has(cacheKey) && cacheTimestamps.has(cacheKey)) {
      const cacheTime = cacheTimestamps.get(cacheKey)!;
      if (now - cacheTime < CACHE_DURATION) {
        return requestCache.get(cacheKey);
      } else {
        requestCache.delete(cacheKey);
        cacheTimestamps.delete(cacheKey);
      }
    }

    // Phase 8.2: Set granular loading state
    // Track search context so load-more results from stale searches are discarded
    set((s) => {
      const newLoadingStates = { ...s.loadingStates, storefronts: !isLoadMore };
      const newSectionErrors = { ...s.sectionErrors, storefronts: null };
      return {
        loadingStates: newLoadingStates,
        sectionErrors: newSectionErrors,
        isLoading: computeIsLoading(newLoadingStates),
        error: computeError(newSectionErrors),
        // Update search context on fresh searches (not load-more) so in-flight
        // load-more requests from old search context get discarded
        ...(!isLoadMore ? { storefrontsSearch: searchContext } : {}),
      };
    });

    const fetchPromise = (async () => {
      try {
        const params = new URLSearchParams();
        params.set("mode", "seller");
        if (search) params.set("search", search);
        params.set("limit", String(limit));
        params.set("offset", String(offset));

        const data = await apiClient.get<{
          storefronts: Storefront[];
          total_count: number;
          mode: string;
        }>(`/api/storefronts?${params.toString()}`);
        const fetched = data.storefronts || [];

        set((s) => {
          // Race condition guard: if a new search fired while this load-more was in-flight,
          // the store's search context has changed — discard these stale results
          if (isLoadMore && s.storefrontsSearch !== searchContext) {
            const newLoadingStates = { ...s.loadingStates, storefronts: false };
            return { loadingStates: newLoadingStates, isLoading: computeIsLoading(newLoadingStates) };
          }

          const newLoadingStates = { ...s.loadingStates, storefronts: false };
          const merged = isLoadMore ? [...s.storefronts, ...fetched] : fetched;
          const primaryStorefront = merged[0] || null;
          return {
            storefronts: merged,
            storefrontsTotal: data.total_count || 0,
            storefront: s.storefront || primaryStorefront,
            loadingStates: newLoadingStates,
            isLoading: computeIsLoading(newLoadingStates),
            storefrontsLoadedAt: offset === 0 ? now : s.storefrontsLoadedAt,
            storefrontsLabelTag: labelTag,
            storefrontsSearch: searchContext,
          };
        });

        return data;
      } catch (error: any) {
        console.error("Error fetching label storefronts:", error);
        set((s) => {
          const newLoadingStates = { ...s.loadingStates, storefronts: false };
          const newSectionErrors = { ...s.sectionErrors, storefronts: error.message };
          return {
            ...(isLoadMore ? {} : { storefronts: [], storefront: null, storefrontsTotal: 0 }),
            loadingStates: newLoadingStates,
            sectionErrors: newSectionErrors,
            isLoading: computeIsLoading(newLoadingStates),
            error: computeError(newSectionErrors),
          };
        });
        throw error;
      } finally {
        requestCache.delete(cacheKey);
      }
    })();

    requestCache.set(cacheKey, fetchPromise);
    cacheTimestamps.set(cacheKey, now);

    return fetchPromise;
  },

  fetchStorefrontByShareToken: async (slug: string, shareToken: string, options?: { tracksLimit?: number }) => {
    // Keyed by viewer: the rows differ by who asks. Two calls for the same viewer still share
    // one request (React StrictMode mounts the page's effect twice).
    const viewer = await storefrontViewerKey();
    const cacheKey = `storefront-share-${slug}-${shareToken}|${viewer}`;
    const now = Date.now();

    // Check if we have a recent cached request
    if (requestCache.has(cacheKey) && cacheTimestamps.has(cacheKey)) {
      const cacheTime = cacheTimestamps.get(cacheKey)!;
      if (now - cacheTime < CACHE_DURATION) {
        return requestCache.get(cacheKey);
      } else {
        // Remove expired cache
        requestCache.delete(cacheKey);
        cacheTimestamps.delete(cacheKey);
      }
    }

    // Track the currently requested token to prevent stale overwrites
    // If user navigates A → B and A's fetch completes after B's, we discard A's data
    const requestedToken = shareToken;
    const isLatest = startRead("share");

    // Phase 8.2: Set granular loading state for storefront
    // Also clear stale data to prevent showing wrong storefront during load
    set((s) => {
      const newLoadingStates = { ...s.loadingStates, storefront: true };
      const newSectionErrors = { ...s.sectionErrors, storefront: null };
      // Clear storefront if it doesn't match what we're fetching (prevents stale data flash)
      const shouldClearStorefront = s.storefront && s.storefront.share_token !== shareToken;
      return {
        loadingStates: newLoadingStates,
        sectionErrors: newSectionErrors,
        isLoading: computeIsLoading(newLoadingStates),
        error: computeError(newSectionErrors),
        // Clear stale storefront data so UI shows loading instead of wrong data
        ...(shouldClearStorefront ? { storefront: null, tracks: [], artists: [], albums: [] } : {}),
      };
    });

    const fetchPromise = (async () => {
      try {
        // Fetch storefront and tracks from backend share endpoint using apiClient
        // URL format: /api/storefront/share/{slug}/{token}?tracks_limit=N
        const params = new URLSearchParams();
        if (options?.tracksLimit !== undefined) {
          params.set("tracks_limit", String(options.tracksLimit));
        }
        const queryString = params.toString();
        const url = `/api/storefront/share/${slug}/${shareToken}${queryString ? `?${queryString}` : ""}`;

        const data = await apiClient.get<{
          storefront: any;
          tracks: any[];
          tracks_total: number;
          tracks_has_more: boolean;
        }>(url);
        const storefrontData = data.storefront as Storefront;
        const tracksData = data.tracks || [];
        const mappedTracks: Track[] = tracksData.map(mapDatabaseTrack);

        // A newer read (another viewer's, or after reset()) owns the store now.
        if (!isLatest()) return data;

        // RACE CONDITION FIX: Only write data if this is still the current request
        // Check if user has navigated to a different storefront while we were fetching
        // NOTE: Don't use loadingStates.storefront in this check - it can be set to false
        // by a different request completing, causing valid data to be incorrectly discarded
        const currentState = get();
        const isStillRelevant =
          !currentState.storefront || // No storefront loaded yet - always accept
          currentState.storefront.share_token === requestedToken || // Same storefront we fetched
          currentState.loadingStates.storefront; // Still in loading state (accepting new data)

        if (!isStillRelevant) {
          // User navigated to a different storefront and it finished loading
          // Discard this stale data to avoid overwriting newer data
          console.log('[storefront] Discarding stale fetch result');
          // Don't touch loading state - the newer request handles it
          return data;
        }

        // Phase 8.2: Clear loading state on success
        // Phase C: Store tracksTotal for stats display
        set((s) => {
          const newLoadingStates = { ...s.loadingStates, storefront: false };
          return {
            storefront: storefrontData,
            tracks: mappedTracks,
            tracksTotal: data.tracks_total ?? mappedTracks.length,
            loadingStates: newLoadingStates,
            isLoading: computeIsLoading(newLoadingStates),
          };
        });

        return data;
      } catch (error: any) {
        console.error("Error fetching shared storefront:", error);
        if (!isLatest()) throw error;
        // Phase 8.2: Set section-specific error
        set((s) => {
          const newLoadingStates = { ...s.loadingStates, storefront: false };
          const newSectionErrors = { ...s.sectionErrors, storefront: error.message };
          return {
            loadingStates: newLoadingStates,
            sectionErrors: newSectionErrors,
            isLoading: computeIsLoading(newLoadingStates),
            error: computeError(newSectionErrors),
          };
        });
        throw error;
      } finally {
        // Clean up cache after request completes
        requestCache.delete(cacheKey);
      }
    })();

    // Cache the promise AND timestamp together to prevent duplicate requests
    // (Setting cacheTimestamps here, not inside the success handler, ensures
    // React StrictMode's double-mount finds the cache entry on the second call)
    requestCache.set(cacheKey, fetchPromise);
    cacheTimestamps.set(cacheKey, now);

    return fetchPromise;
  },

  fetchStorefrontById: async (storefrontId: string) => {
    // Keyed by viewer, as fetchStorefrontByShareToken is: the rows differ by who asks.
    const cacheKey = storefrontByIdKey(storefrontId, await storefrontViewerKey());
    const now = Date.now();

    // Check if we have a recent cached request
    if (requestCache.has(cacheKey) && cacheTimestamps.has(cacheKey)) {
      const cacheTime = cacheTimestamps.get(cacheKey)!;
      if (now - cacheTime < CACHE_DURATION) {
        return requestCache.get(cacheKey);
      } else {
        requestCache.delete(cacheKey);
        cacheTimestamps.delete(cacheKey);
      }
    }

    const isLatest = startRead("byId");

    // Phase 8.2: Set granular loading state for storefront
    set((s) => {
      const newLoadingStates = { ...s.loadingStates, storefront: true };
      const newSectionErrors = { ...s.sectionErrors, storefront: null };
      return {
        loadingStates: newLoadingStates,
        sectionErrors: newSectionErrors,
        isLoading: computeIsLoading(newLoadingStates),
        error: computeError(newSectionErrors),
      };
    });

    const fetchPromise = (async () => {
      try {
        // Fetch storefront by ID from backend endpoint
        // Backend returns is_owner at top level, not inside storefront object
        const data = await apiClient.get<{
          storefront: Storefront;
          tracks: any[];
          is_owner: boolean;
        }>(`/api/storefronts/id/${storefrontId}`);

        // Merge is_owner into storefront object to create StorefrontDetail
        const storefrontData: StorefrontDetail = {
          ...data.storefront,
          is_owner: data.is_owner ?? false,
        };
        const tracksData = data.tracks || [];
        const mappedTracks: Track[] = tracksData.map(mapDatabaseTrack);

        // A newer read (another viewer's, or after reset()) owns the store now.
        if (!isLatest()) return storefrontData;

        // Phase 8.2: Clear loading state on success
        set((s) => {
          const newLoadingStates = { ...s.loadingStates, storefront: false };
          return {
            storefront: storefrontData,
            tracks: mappedTracks,
            loadingStates: newLoadingStates,
            isLoading: computeIsLoading(newLoadingStates),
          };
        });

        return storefrontData;
      } catch (error: any) {
        console.error("Error fetching storefront by ID:", error);
        if (!isLatest()) return null;
        // Phase 8.2: Set section-specific error
        set((s) => {
          const newLoadingStates = { ...s.loadingStates, storefront: false };
          const newSectionErrors = { ...s.sectionErrors, storefront: error.message };
          return {
            loadingStates: newLoadingStates,
            sectionErrors: newSectionErrors,
            isLoading: computeIsLoading(newLoadingStates),
            error: computeError(newSectionErrors),
          };
        });
        return null;
      } finally {
        requestCache.delete(cacheKey);
      }
    })();

    // The promise and its timestamp together, as fetchStorefrontByShareToken does: the check above
    // needs both, so a second call while this one is in flight (React StrictMode's double mount)
    // shares it instead of sending its own.
    requestCache.set(cacheKey, fetchPromise);
    cacheTimestamps.set(cacheKey, now);
    return fetchPromise;
  },

  fetchPublicStorefronts: async (search?: string, limit: number = 50, offset: number = 0, force: boolean = false) => {
    const searchContext = search || "";
    const cacheKey = `public-storefronts-${searchContext}-${limit}-${offset}`;
    const now = Date.now();
    const state = get();
    const isLoadMore = offset > 0;

    // Cache check: skip when loading more, forced, or search context changed
    if (!force && !isLoadMore && state.publicStorefronts.length > 0 && state.publicStorefrontsLoadedAt && state.publicStorefrontsSearch === searchContext) {
      if (now - state.publicStorefrontsLoadedAt < CACHE_DURATION) {
        return;
      }
    }

    if (force) {
      requestCache.delete(cacheKey);
      cacheTimestamps.delete(cacheKey);
    }

    // In-flight deduplication
    if (requestCache.has(cacheKey) && cacheTimestamps.has(cacheKey)) {
      const cacheTime = cacheTimestamps.get(cacheKey)!;
      if (now - cacheTime < CACHE_DURATION) {
        return requestCache.get(cacheKey);
      } else {
        requestCache.delete(cacheKey);
        cacheTimestamps.delete(cacheKey);
      }
    }

    // Set loading state (don't show full shimmer on load-more)
    // Track search context so load-more results from stale searches are discarded
    set((s) => {
      const newLoadingStates = { ...s.loadingStates, publicStorefronts: !isLoadMore };
      const newSectionErrors = { ...s.sectionErrors, publicStorefronts: null };
      return {
        loadingStates: newLoadingStates,
        sectionErrors: newSectionErrors,
        isLoading: computeIsLoading(newLoadingStates),
        error: computeError(newSectionErrors),
        ...(!isLoadMore ? { publicStorefrontsSearch: searchContext } : {}),
      };
    });

    const fetchPromise = (async () => {
      try {
        const params = new URLSearchParams();
        params.set("mode", "buyer");
        if (search) params.set("search", search);
        params.set("limit", String(limit));
        params.set("offset", String(offset));

        const data = await apiClient.get<{
          storefronts: StorefrontListItem[];
          total_count: number;
          mode: string;
        }>(`/api/storefronts?${params.toString()}`);

        set((s) => {
          // Race condition guard: discard stale load-more results
          if (isLoadMore && s.publicStorefrontsSearch !== searchContext) {
            const newLoadingStates = { ...s.loadingStates, publicStorefronts: false };
            return { loadingStates: newLoadingStates, isLoading: computeIsLoading(newLoadingStates) };
          }

          const newLoadingStates = { ...s.loadingStates, publicStorefronts: false };
          const fetched = data.storefronts || [];
          const merged = isLoadMore ? [...s.publicStorefronts, ...fetched] : fetched;
          return {
            publicStorefronts: merged,
            publicStorefrontsTotal: data.total_count || 0,
            loadingStates: newLoadingStates,
            isLoading: computeIsLoading(newLoadingStates),
            publicStorefrontsLoadedAt: offset === 0 ? now : s.publicStorefrontsLoadedAt,
            publicStorefrontsSearch: searchContext,
          };
        });

        return data;
      } catch (error: any) {
        console.error("Error fetching public storefronts:", error);
        set((s) => {
          const newLoadingStates = { ...s.loadingStates, publicStorefronts: false };
          const newSectionErrors = { ...s.sectionErrors, publicStorefronts: error.message };
          return {
            ...(isLoadMore ? {} : { publicStorefronts: [], publicStorefrontsTotal: 0 }),
            loadingStates: newLoadingStates,
            sectionErrors: newSectionErrors,
            isLoading: computeIsLoading(newLoadingStates),
            error: computeError(newSectionErrors),
          };
        });
      } finally {
        requestCache.delete(cacheKey);
      }
    })();

    requestCache.set(cacheKey, fetchPromise);
    cacheTimestamps.set(cacheKey, now);
    return fetchPromise;
  },

  createStorefront: async (data: CreateStorefrontInput) => {
    try {
      const result = await apiClient.post<{ storefront: Storefront }>("/api/storefronts", {
        name: data.name,
        description: data.description || null,
        add_all_tracks: data.addAllTracks || false
      });

      const newStorefront = result.storefront;

      // Add to local storefronts list and increment total
      set((s) => ({
        storefronts: [...s.storefronts, newStorefront],
        storefrontsTotal: s.storefrontsTotal + 1,
        storefront: newStorefront,
        // Invalidate cache so next fetch gets fresh data
        storefrontsLoadedAt: null,
      }));

      return newStorefront;
    } catch (error: any) {
      console.error("Error creating storefront:", error);
      set({ error: error.message });
      return null;
    }
  },

  updateStorefront: async (storefrontId: string, updates: { name?: string; description?: string; is_public?: boolean; accent?: StorefrontAccent }) => {
    try {
      const result = await apiClient.patch<{ storefront: Storefront }>(`/api/storefronts/${storefrontId}`, updates);

      const updatedStorefront = result.storefront;

      // Update local state
      set((state) => ({
        storefront: state.storefront?.id === storefrontId
          ? { ...state.storefront, ...updatedStorefront }
          : state.storefront,
        storefronts: state.storefronts.map((sf) =>
          sf.id === storefrontId ? { ...sf, ...updatedStorefront } : sf
        )
      }));

      // Clear relevant caches
      forgetStorefrontById(storefrontId);

      return updatedStorefront;
    } catch (error: any) {
      console.error("Error updating storefront:", error);
      set({ error: error.message });
      return null;
    }
  },

  // The page takes the new colour at once; a failed save puts the old one back.
  setStorefrontAccent: async (storefrontId: string, accent: StorefrontAccent) => {
    const previous = get().storefront?.id === storefrontId ? get().storefront?.accent : undefined;
    const apply = (value: StorefrontAccent | undefined) =>
      set((state) =>
        state.storefront?.id === storefrontId ? { storefront: { ...state.storefront, accent: value } } : {}
      );
    apply(accent);
    const saved = await get().updateStorefront(storefrontId, { accent });
    if (!saved) apply(previous);
    return Boolean(saved);
  },

  deleteStorefront: async (storefrontId: string) => {
    try {
      // Get the label_tag before deletion for cache invalidation
      const currentState = get();
      const storefrontToDelete = currentState.storefronts.find((sf) => sf.id === storefrontId)
        || (currentState.storefront?.id === storefrontId ? currentState.storefront : null);
      const labelTag = storefrontToDelete?.label_tag;

      await apiClient.delete(`/api/storefronts/${storefrontId}`);

      // Remove from local state and decrement totals
      set((s) => {
        const newStorefronts = s.storefronts.filter((sf) => sf.id !== storefrontId);
        const newPublicStorefronts = s.publicStorefronts.filter((sf) => sf.id !== storefrontId);
        const removedFromSeller = newStorefronts.length < s.storefronts.length;
        const removedFromPublic = newPublicStorefronts.length < s.publicStorefronts.length;
        return {
          storefronts: newStorefronts,
          storefrontsTotal: removedFromSeller ? Math.max(0, s.storefrontsTotal - 1) : s.storefrontsTotal,
          storefront: s.storefront?.id === storefrontId ? null : s.storefront,
          publicStorefronts: newPublicStorefronts,
          publicStorefrontsTotal: removedFromPublic ? Math.max(0, s.publicStorefrontsTotal - 1) : s.publicStorefrontsTotal,
        };
      });

      // Clear caches — invalidate by resetting timestamps so next fetch is fresh
      forgetStorefrontById(storefrontId);
      // Reset loaded-at timestamps to force re-fetch on next navigation
      set({ storefrontsLoadedAt: null, publicStorefrontsLoadedAt: null });

      return true;
    } catch (error: any) {
      console.error("Error deleting storefront:", error);
      set({ error: error.message });
      return false;
    }
  },

  fetchStorefrontArtists: async (storefrontId: string, options?: { limit?: number; search?: string; shareToken?: string }) => {
    const cacheKey = `storefront-artists-${storefrontId}-${options?.limit ?? "all"}-${options?.search ?? ""}`;
    const now = Date.now();

    // Check if we have a recent cached request
    if (requestCache.has(cacheKey) && cacheTimestamps.has(cacheKey)) {
      const cacheTime = cacheTimestamps.get(cacheKey)!;
      if (now - cacheTime < CACHE_DURATION) {
        return requestCache.get(cacheKey);
      } else {
        // Remove expired cache
        requestCache.delete(cacheKey);
        cacheTimestamps.delete(cacheKey);
      }
    }

    // Phase 8.2: Set granular loading state for artists (independent of other sections)
    set((s) => {
      const newLoadingStates = { ...s.loadingStates, artists: true };
      const newSectionErrors = { ...s.sectionErrors, artists: null };
      return {
        loadingStates: newLoadingStates,
        sectionErrors: newSectionErrors,
        isLoading: computeIsLoading(newLoadingStates),
        error: computeError(newSectionErrors),
      };
    });

    const fetchPromise = (async () => {
      try {
        // Fetch artists from backend endpoint using apiClient
        // Phase C: Support limit parameter for quick initial load
        const params = new URLSearchParams();
        if (options?.limit !== undefined) {
          params.set("limit", String(options.limit));
        }
        if (options?.search && options.search.trim()) {
          params.set("search", options.search.trim());
        }
        if (options?.shareToken) {
          params.set("share_token", options.shareToken);
        }
        const queryString = params.toString();
        const url = `/api/storefronts/${storefrontId}/artists${queryString ? `?${queryString}` : ""}`;

        const data = await apiClient.get<{
          artists: any[];
          total: number;
          has_more: boolean;
        }>(url);
        const artistsData = data.artists || [];

        // Map backend response to ArtistView format
        const mappedArtists: ArtistView[] = artistsData.map((dbArtist: any) => ({
          id: dbArtist.id,
          name: dbArtist.name || "",
          coverUrl: dbArtist.profile_image_url || "",
          avatarUrl: dbArtist.profile_image_url || "",
          fallbackAvatarUrl: `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(dbArtist.name || "artist")}&backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf&scale=80`,
          tracks: [], // Tracks are not included in this endpoint (use existing track list)
          genres: Array.isArray(dbArtist.genres) ? dbArtist.genres : [],
          totalTracks: dbArtist.track_count || 0,
          totalAlbums: dbArtist.album_count || 0,
          earliestRelease: dbArtist.earliest_release || "",
          latestRelease: dbArtist.latest_release || "",
          createdAt: "",        // Not provided by backend yet
          updatedAt: "",        // Not provided by backend yet
          isPublic: true,       // Artists in storefront are considered public
          isVirtual: true as const,
          artistName: dbArtist.name || "",
        }));

        // Phase 8.2: Clear loading state on success (doesn't affect other sections)
        // Phase C: Store artistsTotal for stats display
        set((s) => {
          const newLoadingStates = { ...s.loadingStates, artists: false };
          return {
            artists: mappedArtists,
            artistsTotal: data.total ?? mappedArtists.length,
            loadingStates: newLoadingStates,
            isLoading: computeIsLoading(newLoadingStates),
          };
        });

        // Cache the successful result
        cacheTimestamps.set(cacheKey, now);

        return data;
      } catch (error: any) {
        console.error("Error fetching storefront artists:", error);
        // Phase 8.2: Set section-specific error (allows partial data display)
        set((s) => {
          const newLoadingStates = { ...s.loadingStates, artists: false };
          const newSectionErrors = { ...s.sectionErrors, artists: error.message };
          return {
            artists: [],
            loadingStates: newLoadingStates,
            sectionErrors: newSectionErrors,
            isLoading: computeIsLoading(newLoadingStates),
            error: computeError(newSectionErrors),
          };
        });
        throw error;
      } finally {
        // Clean up cache after request completes
        requestCache.delete(cacheKey);
      }
    })();

    // Cache the promise to prevent duplicate requests
    requestCache.set(cacheKey, fetchPromise);

    return fetchPromise;
  },

  fetchStorefrontAlbums: async (storefrontId: string, options?: { limit?: number; search?: string; shareToken?: string }) => {
    const cacheKey = `storefront-albums-${storefrontId}-${options?.limit ?? "all"}-${options?.search ?? ""}`;
    const now = Date.now();

    // Check if we have a recent cached request
    if (requestCache.has(cacheKey) && cacheTimestamps.has(cacheKey)) {
      const cacheTime = cacheTimestamps.get(cacheKey)!;
      if (now - cacheTime < CACHE_DURATION) {
        return requestCache.get(cacheKey);
      } else {
        // Remove expired cache
        requestCache.delete(cacheKey);
        cacheTimestamps.delete(cacheKey);
      }
    }

    // Phase 8.2: Set granular loading state for albums (independent of other sections)
    set((s) => {
      const newLoadingStates = { ...s.loadingStates, albums: true };
      const newSectionErrors = { ...s.sectionErrors, albums: null };
      return {
        loadingStates: newLoadingStates,
        sectionErrors: newSectionErrors,
        isLoading: computeIsLoading(newLoadingStates),
        error: computeError(newSectionErrors),
      };
    });

    const fetchPromise = (async () => {
      try {
        // Fetch albums from backend endpoint using apiClient
        // Phase C: Support limit parameter for quick initial load
        const params = new URLSearchParams();
        if (options?.limit !== undefined) {
          params.set("limit", String(options.limit));
        }
        if (options?.search && options.search.trim()) {
          params.set("search", options.search.trim());
        }
        if (options?.shareToken) {
          params.set("share_token", options.shareToken);
        }
        const queryString = params.toString();
        const url = `/api/storefronts/${storefrontId}/albums${queryString ? `?${queryString}` : ""}`;

        const data = await apiClient.get<{
          albums: any[];
          total: number;
          has_more: boolean;
        }>(url);
        const albumsData = data.albums || [];

        // Map backend response to AlbumView format
        const mappedAlbums: AlbumView[] = albumsData.map((dbAlbum: any) => ({
          id: dbAlbum.id,
          title: dbAlbum.title || "Unknown Album",
          artist: dbAlbum.artist_name || "",
          coverUrl: dbAlbum.cover_art_url || "",
          releaseDate: dbAlbum.release_date || "",
          tracks: [], // Tracks are not included in this endpoint (use existing track list)
          genre: dbAlbum.genre || "Unknown",  // Backend returns single genre string from RPC
          description: "",  // Not provided by backend
          isPublic: true,   // Albums in storefront are considered public
          type: "album" as const,
          label: "",        // Not provided by backend
          totalTracks: dbAlbum.track_count || 0,
          totalLength: dbAlbum.total_duration || 0,  // Duration in milliseconds from backend
          createdAt: "",    // Not provided by backend
          updatedAt: "",    // Not provided by backend
          isVirtual: true as const,
          albumName: dbAlbum.title || "Unknown Album",
          albumCategory: dbAlbum.album_category || undefined,
        }));

        // Phase 8.2: Clear loading state on success (doesn't affect other sections)
        // Phase C: Store albumsTotal for stats display
        set((s) => {
          const newLoadingStates = { ...s.loadingStates, albums: false };
          return {
            albums: mappedAlbums,
            albumsTotal: data.total ?? mappedAlbums.length,
            loadingStates: newLoadingStates,
            isLoading: computeIsLoading(newLoadingStates),
          };
        });

        // Cache the successful result
        cacheTimestamps.set(cacheKey, now);

        return data;
      } catch (error: any) {
        console.error("Error fetching storefront albums:", error);
        // Phase 8.2: Set section-specific error (allows partial data display)
        set((s) => {
          const newLoadingStates = { ...s.loadingStates, albums: false };
          const newSectionErrors = { ...s.sectionErrors, albums: error.message };
          return {
            albums: [],
            loadingStates: newLoadingStates,
            sectionErrors: newSectionErrors,
            isLoading: computeIsLoading(newLoadingStates),
            error: computeError(newSectionErrors),
          };
        });
        throw error;
      } finally {
        // Clean up cache after request completes
        requestCache.delete(cacheKey);
      }
    })();

    // Cache the promise to prevent duplicate requests
    requestCache.set(cacheKey, fetchPromise);

    return fetchPromise;
  },

  addTracksToCatalog: async (storefrontId: string, trackIds: string[]) => {
    try {
      // Call backend endpoint to add tracks using apiClient
      const result = await apiClient.post<any>(`/api/storefronts/${storefrontId}/catalog`, {
        track_ids: trackIds,
      });

      await get().refreshStorefrontPreview();

      return { success: true };
    } catch (error: any) {
      console.error("Error adding tracks to catalog:", error);
      return { success: false, error: error.message };
    }
  },

  removeTrackFromCatalog: async (storefrontId: string, trackId: string) => {
    try {
      // Call backend endpoint to remove track (Phase 6: API Redesign)
      await apiClient.delete(`/api/storefronts/${storefrontId}/tracks/${trackId}`);

      await get().refreshStorefrontPreview();

      return { success: true };
    } catch (error: any) {
      console.error("Error removing track from catalog:", error);
      return { success: false, error: error.message };
    }
  },

  // The catalog changed: reload what the storefront page shows (its track preview, the
  // track/artist/album totals and the shelves) at the page's own sizes, past the request
  // cache, so a count never lags a change.
  refreshStorefrontPreview: async () => {
    const current = get().storefront;
    if (!current?.slug || !current?.share_token) return;
    const { id, slug, share_token: shareToken } = current;
    // The share read is cached per viewer (its key ends in "|<viewer>"): every viewer's copy goes.
    forgetCachedReads(`storefront-share-${slug}-${shareToken}|`);
    const stale = [
      `storefront-artists-${id}-${STOREFRONT_SHELF_PREVIEW_LIMIT}-`,
      `storefront-albums-${id}-${STOREFRONT_SHELF_PREVIEW_LIMIT}-`,
    ];
    stale.forEach((key) => {
      requestCache.delete(key);
      cacheTimestamps.delete(key);
    });
    await Promise.all([
      get().fetchStorefrontByShareToken(slug, shareToken, { tracksLimit: STOREFRONT_TRACKS_PREVIEW_LIMIT }),
      get().fetchStorefrontArtists(id, { limit: STOREFRONT_SHELF_PREVIEW_LIMIT, shareToken }),
      get().fetchStorefrontAlbums(id, { limit: STOREFRONT_SHELF_PREVIEW_LIMIT, shareToken }),
    ]);
  },

  toggleTrackFeatured: async (storefrontId: string, trackId: string, isFeatured: boolean) => {
    try {
      await apiClient.patch(`/api/storefronts/${storefrontId}/tracks/${trackId}/featured`, {
        is_featured: isFeatured,
      });

      // Update local tracks state to reflect the change
      // Use String() comparison because Track.id can be number | string
      set((state) => ({
        tracks: state.tracks.map((t) =>
          String(t.id) === trackId ? { ...t, is_featured: isFeatured } : t
        ),
      }));

      return { success: true };
    } catch (error: any) {
      console.error("Error toggling track featured:", error);
      return { success: false, error: error.message };
    }
  },

  publishStorefront: async (storefrontId: string, isPublic: boolean) => {
    try {
      // Use backend endpoint (Phase 6: API Redesign)
      await apiClient.patch(`/api/storefronts/${storefrontId}`, { is_public: isPublic });

      // Update local state
      set((state) => ({
        storefront: state.storefront
          ? { ...state.storefront, is_public: isPublic }
          : null,
      }));

      return { success: true };
    } catch (error: any) {
      console.error("Error updating storefront:", error);
      return { success: false, error: error.message };
    }
  },

  updateStorefrontDescription: async (storefrontId: string, description: string) => {
    try {
      // Use backend endpoint (Phase 6: API Redesign)
      await apiClient.patch(`/api/storefronts/${storefrontId}`, { description });

      set((state) => ({
        storefront: state.storefront && state.storefront.id === storefrontId
          ? { ...state.storefront, description }
          : state.storefront,
      }));

      return { success: true };
    } catch (error: any) {
      console.error("Error updating storefront description:", error);
      return { success: false, error: error.message };
    }
  },

  // Phase B: Browse pages pagination actions
  fetchBrowseTracks: async (storefrontId: string, reset: boolean = true, shareToken?: string, search?: string) => {
    // The rows depend on who asks (public columns unless the caller is fully set up).
    const viewer = await storefrontViewerKey();
    const state = get();
    const browseState = state.browse.tracks;
    const PAGE_SIZE = 30;

    // If not resetting and already loading, skip
    if (!reset && browseState.isFetchingMore) return;

    // If resetting and already loading initial for this viewer, skip. A first page in flight for
    // another viewer (who just signed in or out) doesn't count: this read replaces it.
    if (reset && browseState.isLoading && browseTracksViewer === viewer) return;

    const isLatest = reset ? startRead("browseTracks") : joinRead("browseTracks");
    if (reset) browseTracksViewer = viewer;

    // Calculate offset
    const offset = reset ? 0 : browseState.offset;

    // Set loading state
    set((s) => ({
      browse: {
        ...s.browse,
        tracks: {
          ...s.browse.tracks,
          isLoading: reset,
          isFetchingMore: !reset,
          error: null,
          storefrontId,
        },
      },
    }));

    try {
      // Build URL with optional share_token and search for viewer access
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));
      if (shareToken) {
        params.set("share_token", shareToken);
      }
      if (search && search.trim()) {
        params.set("search", search.trim());
      }

      const data = await apiClient.get<{
        tracks: any[];
        total: number;
        has_more: boolean;
      }>(`/api/storefronts/${storefrontId}/tracks?${params.toString()}`);

      const mappedTracks: Track[] = (data.tracks || []).map(mapDatabaseTrack);
      const newOffset = offset + mappedTracks.length;

      // A newer first page (another viewer's, or after reset()) owns the list now.
      if (!isLatest()) return;

      set((s) => ({
        browse: {
          ...s.browse,
          tracks: {
            items: reset ? mappedTracks : [...s.browse.tracks.items, ...mappedTracks],
            total: data.total || 0,
            offset: newOffset,
            hasMore: data.has_more ?? false,
            isLoading: false,
            isFetchingMore: false,
            error: null,
            storefrontId,
          },
        },
      }));
    } catch (error: any) {
      console.error("Error fetching browse tracks:", error);
      if (!isLatest()) return;
      set((s) => ({
        browse: {
          ...s.browse,
          tracks: {
            ...s.browse.tracks,
            isLoading: false,
            isFetchingMore: false,
            error: error.message,
          },
        },
      }));
    }
  },

  fetchBrowseArtists: async (storefrontId: string, reset: boolean = true, shareToken?: string, search?: string) => {
    const state = get();
    const browseState = state.browse.artists;
    const PAGE_SIZE = 30;

    if (!reset && browseState.isFetchingMore) return;
    if (reset && browseState.isLoading) return;

    const offset = reset ? 0 : browseState.offset;

    set((s) => ({
      browse: {
        ...s.browse,
        artists: {
          ...s.browse.artists,
          isLoading: reset,
          isFetchingMore: !reset,
          error: null,
          storefrontId,
        },
      },
    }));

    try {
      // Build URL with optional share_token and search for viewer access
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));
      if (shareToken) {
        params.set("share_token", shareToken);
      }
      if (search && search.trim()) {
        params.set("search", search.trim());
      }

      const data = await apiClient.get<{
        artists: any[];
        total: number;
        has_more: boolean;
      }>(`/api/storefronts/${storefrontId}/artists?${params.toString()}`);

      const mappedArtists: ArtistView[] = (data.artists || []).map((dbArtist: any) => ({
        id: dbArtist.id,
        name: dbArtist.name || "",
        coverUrl: dbArtist.profile_image_url || "",
        avatarUrl: dbArtist.profile_image_url || "",
        fallbackAvatarUrl: `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(dbArtist.name || "artist")}&backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf&scale=80`,
        tracks: [],
        genres: Array.isArray(dbArtist.genres) ? dbArtist.genres : [],
        totalTracks: dbArtist.track_count || 0,
        totalAlbums: dbArtist.album_count || 0,
        earliestRelease: dbArtist.earliest_release || "",
        latestRelease: dbArtist.latest_release || "",
        createdAt: "",
        updatedAt: "",
        isPublic: true,
        isVirtual: true as const,
        artistName: dbArtist.name || "",
      }));

      const newOffset = offset + mappedArtists.length;

      set((s) => ({
        browse: {
          ...s.browse,
          artists: {
            items: reset ? mappedArtists : [...s.browse.artists.items, ...mappedArtists],
            total: data.total || 0,
            offset: newOffset,
            hasMore: data.has_more ?? false,
            isLoading: false,
            isFetchingMore: false,
            error: null,
            storefrontId,
          },
        },
      }));
    } catch (error: any) {
      console.error("Error fetching browse artists:", error);
      set((s) => ({
        browse: {
          ...s.browse,
          artists: {
            ...s.browse.artists,
            isLoading: false,
            isFetchingMore: false,
            error: error.message,
          },
        },
      }));
    }
  },

  fetchBrowseAlbums: async (storefrontId: string, reset: boolean = true, shareToken?: string, search?: string) => {
    const state = get();
    const browseState = state.browse.albums;
    const PAGE_SIZE = 30;

    if (!reset && browseState.isFetchingMore) return;
    if (reset && browseState.isLoading) return;

    const offset = reset ? 0 : browseState.offset;

    set((s) => ({
      browse: {
        ...s.browse,
        albums: {
          ...s.browse.albums,
          isLoading: reset,
          isFetchingMore: !reset,
          error: null,
          storefrontId,
        },
      },
    }));

    try {
      // Build URL with optional share_token and search for viewer access
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));
      if (shareToken) {
        params.set("share_token", shareToken);
      }
      if (search && search.trim()) {
        params.set("search", search.trim());
      }

      const data = await apiClient.get<{
        albums: any[];
        total: number;
        has_more: boolean;
      }>(`/api/storefronts/${storefrontId}/albums?${params.toString()}`);

      const mappedAlbums: AlbumView[] = (data.albums || []).map((dbAlbum: any) => ({
        id: dbAlbum.id,
        title: dbAlbum.title || "Unknown Album",
        artist: dbAlbum.artist_name || "",
        coverUrl: dbAlbum.cover_art_url || "",
        releaseDate: dbAlbum.release_date || "",
        tracks: [],
        genre: dbAlbum.genre || "Unknown",
        description: "",
        isPublic: true,
        type: "album" as const,
        label: "",
        totalTracks: dbAlbum.track_count || 0,
        totalLength: dbAlbum.total_duration || 0,
        createdAt: "",
        updatedAt: "",
        isVirtual: true as const,
        albumName: dbAlbum.title || "Unknown Album",
        albumCategory: dbAlbum.album_category || undefined,
      }));

      const newOffset = offset + mappedAlbums.length;

      set((s) => ({
        browse: {
          ...s.browse,
          albums: {
            items: reset ? mappedAlbums : [...s.browse.albums.items, ...mappedAlbums],
            total: data.total || 0,
            offset: newOffset,
            hasMore: data.has_more ?? false,
            isLoading: false,
            isFetchingMore: false,
            error: null,
            storefrontId,
          },
        },
      }));
    } catch (error: any) {
      console.error("Error fetching browse albums:", error);
      set((s) => ({
        browse: {
          ...s.browse,
          albums: {
            ...s.browse.albums,
            isLoading: false,
            isFetchingMore: false,
            error: error.message,
          },
        },
      }));
    }
  },

  // Phase B: Load more helpers (convenience wrappers)
  loadMoreBrowseTracks: async (storefrontId: string, shareToken?: string, search?: string) => {
    await get().fetchBrowseTracks(storefrontId, false, shareToken, search);
  },

  loadMoreBrowseArtists: async (storefrontId: string, shareToken?: string, search?: string) => {
    await get().fetchBrowseArtists(storefrontId, false, shareToken, search);
  },

  loadMoreBrowseAlbums: async (storefrontId: string, shareToken?: string, search?: string) => {
    await get().fetchBrowseAlbums(storefrontId, false, shareToken, search);
  },

  reset: () => {
    // Called on sign-out and on an account switch (resetAllStores): nothing cached or in flight
    // for the previous viewer may reach the next one.
    clearStorefrontCache();
    for (const kind of Object.keys(latestRead) as ViewerShapedRead[]) latestRead[kind] += 1;
    browseTracksViewer = null;
    set({
      storefront: null,
      storefronts: [],
      tracks: [],
      artists: [],
      albums: [],
      // Phase 8.2: Reset granular loading and error states
      loadingStates: { ...initialLoadingStates },
      sectionErrors: { ...initialSectionErrors },
      isLoading: false,
      error: null,
      publicStorefronts: [],
      publicStorefrontsTotal: 0,
      storefrontsTotal: 0,
      // Phase 8.1: Reset timestamps and label tracking
      storefrontsLoadedAt: null,
      storefrontsLabelTag: null,
      storefrontsSearch: null,
      publicStorefrontsLoadedAt: null,
      publicStorefrontsSearch: null,
      // Phase B: Reset browse pagination state
      browse: { ...initialBrowse },
      tracksTotal: 0,
      artistsTotal: 0,
      albumsTotal: 0,
    });
  },
}));
